import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { standardsSections } from '@/pages/standards/sections';
import { BreadcrumbBar } from '@/components/editor/BreadcrumbBar';
import { cn } from '@/lib/utils';

/**
 * Workspace-standards layout, hosted under /docs.
 *
 * These are the standards the MCP server serves every connected assistant —
 * Tech Stack, Checks, Model Settings, Rules, Prompt Templates. They used to be
 * tabs on the Connect pillar (formerly /execute); they moved under /docs, where
 * the Docs home already lists them ("What your AI reads"). This layout carries
 * their lateral tab strip so you can move between them without going back to the
 * Docs home. The tabs reuse the `standardsSections` SSOT (whose keys the backend,
 * the MCP contract and the Learn recommendations reference — don't fork it), now
 * pointing at /docs/* paths. Rendered inside ConsolePillars, so it keeps the
 * Workspace + PublishJob providers the editors need.
 */
export default function StandardsLayoutPage() {
  // Carry the query string across the back link — on localhost the ?tenant param
  // IS the host selection, and dropping it would flip the app mid-session.
  const { search } = useLocation();
  return (
    <div className="p-8">
      {/* The same bar every full editor renders (consolidated-full-editor R7),
          bled to the page edges so it reads as chrome, not content. */}
      <BreadcrumbBar
        className="-mx-8 -mt-8 mb-6"
        crumbs={[
          { label: 'Docs', to: { pathname: '/docs', search } },
          { label: 'Standards' },
        ]}
      />
      <div className="mb-6 text-center">
        <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Standards</h1>
        <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
          What your AI reads before it writes a line of code — served to every assistant over MCP.
        </p>
      </div>

      {/* Lateral nav across the standards this workspace defines. */}
      <nav className="mb-6 flex flex-wrap justify-center gap-x-1 border-b border-slate-200 dark:border-slate-800">
        {standardsSections.map(({ to, title, icon: Icon }) => (
          <NavLink
            key={to}
            to={to}
            className={({ isActive }) =>
              cn(
                '-mb-px flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm font-medium transition-colors',
                isActive
                  ? 'border-blue-600 text-blue-600 dark:border-blue-400 dark:text-blue-400'
                  : 'border-transparent text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200',
              )
            }
          >
            <Icon className="h-4 w-4 shrink-0" />
            {title}
          </NavLink>
        ))}
      </nav>

      <Outlet />
    </div>
  );
}
